import { useEffect, useRef, useState } from "react";

import { sound } from "src/client/ai/audio/engine";
import { useGameLog } from "src/client/ai/hooks/selectors";
import { useDispatch, useNet } from "src/client/ai/hooks/useNet";
import { type Level, levelForStage, type Tuning } from "src/engine/ai/difficulty";
import { stagesCleared } from "src/engine/ai/waves";
import type { Side } from "src/api";
import { NOBODY } from "src/engine/drivers";
import { type BoardCanvasHandle } from "./BoardCanvas";
import { Readouts } from "./ReadoutPills";
import { type PauseScreen } from "./PauseMenu";
import { styled } from "./kit";
import { useFlag, useVariant } from "src/client/ai/hooks/useFlag";
import { useDefaultRuleset } from "src/client/ai/hooks/useRuleset";
import { tempoOf } from "src/engine/api/tempo";
import { leadFor, snapSpeed } from "src/client/ai/render/tempo";
import { useHints } from "./useHints";
import { type Tool } from "./tool";
import { useBoardInteraction } from "./useBoardInteraction";
import { endingOf } from "./defeat";
import { despairView, worstDespair } from "src/engine/session/despairView";
import { holdings } from "src/engine/ai/depression";
import { useCelebrations } from "./useCelebrations";
import { useCameraPrefs } from "./cameraPrefs";
import { useClaimSounds, useGameSounds } from "./useGameSounds";
// What kind of game this is, and the board's handle — the two things a zone
// needs that never change per turn (U9, PLAN-UI §28.9). Fed from the shell's
// own values, so `seatedAs` stays the one definition it has always been.
import type { LevelSource } from "src/engine/campaign/levels";
import { ShellModeProvider } from "./shell/ShellMode";
import { BoardHandleProvider } from "./shell/BoardHandle";
// The shell's own state and effects, one subject per file (U9). Each is called
// from here, unconditionally, and owns exactly what it used to own inline.
import { useWatchTransport } from "./useWatchTransport";
import { useScenarioOpening } from "./useScenarioOpening";
import { useEscapeLayers } from "./useEscapeLayers";
import { useCameraLooks } from "./useCameraLooks";
import { useGame } from "./useGame";
import { useOpponentDriver } from "./useOpponentDriver";
// The zones (U9). Each owns the state, the models and the JSX of one corner of
// the screen, reads the board through `useGameState` and the mode through
// `useShellMode`, and takes `board_` as a single prop — one hop, never memoised,
// because `useBoardInteraction` hands back a fresh object every render.
import { HotbarZone } from "./zones/HotbarZone";
import { InspectorZone } from "./zones/InspectorZone";
import { RailZone } from "./zones/RailZone";
import { HandZone } from "./zones/HandZone";
import { TopRight } from "./zones/TopRight";
import { BoardLayer } from "./zones/BoardLayer";
import { BoardOverlays } from "./zones/BoardOverlays";
import { TurnBannerZone } from "./shell/TurnBannerZone";
import { PauseMenuZone } from "./shell/PauseMenuZone";
import { ModalStack } from "./shell/ModalStack";

/**
 * **The board is the page, and the interface floats on it.**
 *
 * This was a three-row grid — header, board, shelf — and every row but the
 * board was a fixed height precisely so the canvas never reflowed: a canvas
 * that changes size reallocates its drawing buffer, and the new buffer starts
 * empty, so anything that resized it showed a black frame. That rule is not
 * relaxed here, it is *satisfied differently* and more strongly. The canvas is
 * now full-bleed and fixed behind everything, so it does not resize when a
 * panel moves, appears, or changes sides — there is no reflow left to protect
 * against.
 *
 * What that buys is the thing the rows could not: the board reads as the
 * subject of a page rather than as the contents of a box, because it is
 * genuinely underneath the whole page rather than beside it.
 */
const Screen = styled("div")(({ theme }) => ({
  height: "100vh",
  position: "relative",
  overflow: "hidden",
  backgroundColor: theme.kit.color.ground,
  color: theme.kit.color.ink,
  fontFamily: theme.kit.font.body
}));

export function GameShell({
  onExit,
  scenario,
  onPrivacy,
  onRestart,
  onLoadLevel,
  level = "easy",
  watching = false,
  tuning,
  teams,
  seed,
  maxFps,
  notice
}: {
  onExit?: () => void;
  /** Set in single player: which starting position, and that a bot plays Black. */
  scenario?: string;
  /** Opens the privacy notice. Threaded to the feedback dialog. */
  onPrivacy?(): void;
  /** Start this mode again — a fresh level, scenario or board, as appropriate. */
  onRestart?(): void;
  /** Open a level in place of the board. The dev panel's, and a run's only. */
  onLoadLevel?(source: LevelSource): void;
  /** How well the opponent plays. Single player only. */
  level?: Level;
  /** Nobody is playing: both armies are driven and the board is a spectacle. */
  watching?: boolean;
  /** Hand-set aggression and greed, from the scenario screen. */
  tuning?: Tuning;
  /**
   * Which team each army is on, indexed by side, for the boards with enough
   * armies to divide. Ignored by the rest, in `scenarioOpening` rather than
   * here.
   */
  teams?: readonly number[];
  /** The opening roll a player asked for, or undefined for a fresh one. */
  seed?: number;
  /**
   * Cap how often the board draws, from the scenario screen. Undefined is off.
   *
   * **Frames, not speed** — the animation speed is a preference in the View
   * menu and applies everywhere, while this is about what the machine is doing
   * with its cores. Offered on the scenario screen because that is where a
   * board gets watched for an hour with a planner searching against the
   * renderer for the same cores; the mechanism itself knows nothing about
   * modes.
   */
  maxFps?: number;
  /**
   * A sentence the game owes the player as the shell opens, said once (§29 U5).
   *
   * Today it is the continued run whose saved history could not be verified.
   * It goes to the pill — the one place a sentence about the game is said —
   * rather than to an overlay: nothing is being asked, and a dialog over a
   * board somebody just came back to would be in the way of the answer.
   */
  notice?: string | null;
}) {
  const dispatch = useDispatch();
  const { net } = useNet();
  const log = useGameLog();
  /**
   * Which pause screen is up, or null for closed (PLAN-UI §28.3).
   *
   * One piece of state for the whole menu, because it is one surface: the old
   * Rules, Costs and Feedback buttons each carried their own flag and each
   * could be open beside the others.
   */
  const [pause, setPause] = useState<PauseScreen | null>(null);

  const board = useRef<BoardCanvasHandle>(null);
  // **What the board does, heard.** Unconditional — watching included, since a
  // spectator hearing a factory work is the point — and the volume sliders are
  // the off switch. Subscribed here rather than in `BoardCanvas` because the
  // listener set outlives every renderer, which is what lets this be mounted
  // before there is a canvas at all.
  useGameSounds(board);
  // And what the hand does: a token arriving is not a board moment.
  useClaimSounds();
  /**
   * The game's beds, and the other half of `ModeMenu`'s effect.
   *
   * `App` renders one screen or the other, so these two moods genuinely
   * alternate and a mood change is always a mount. Empty deps: the request is
   * deduped against what is playing, so a shell remounting — StrictMode, a
   * restart, a new board — cannot restart the track. Silent until `music.ts`
   * has a row (§1.2).
   */
  useEffect(() => {
    sound.music.request("game");
    sound.ambience.request("game");
  }, []);
  /**
   * The one side this client plays, or null when it plays both.
   *
   * Null is hot-seat, where the person at the keyboard is whoever is to move.
   * A network seat pins it, and so does single player — the other army is a
   * bot, and being able to move its pieces on its turn is the same mistake
   * either way. The player is always White.
   *
   * Two things read this and they must agree: what you may move, and which
   * way the camera faces.
   */
  const seatedAs =
    net.seat === "white" ? 0 : net.seat === "black" ? 1 : scenario ? 0 : null;

  /**
   * Watching two machines: you hold no seat at all.
   *
   * `seatedAs` is normally the single answer to both "what may you move" and
   * "which way does the camera face", and those come apart exactly here — the
   * camera still has to point somewhere while you control nothing. `NOBODY` is
   * a side no piece can ever be, so every ownership test the board runs
   * answers "not yours" without a single one of them learning what watching
   * is: no moves offered, no lanes laid, no machine retasked. Selecting still
   * works, because selecting is looking.
   */
  const controlling = watching ? NOBODY : seatedAs;

  /**
   * Whether a drag moves the pieces, lays track or lifts it. **The camera is
   * not one of them any more**: a drag nothing else claims moves the view under
   * every tool, and shift-drag turns it (`tool.ts`).
   *
   * Declared here, above the interaction hook, because the hook takes it: a
   * click on bare ground lays track only while the Build tool is up.
   */
  const [tool, setTool] = useState<Tool>("turn");

  /** The score panel's rollout — see `src/engine/flags/`. Presentation only. */
  const scorePanel = useFlag("score-panel");
  /**
   * Turns between clearing a front and the next camp landing.
   *
   * **Resolved here and handed down as a number**, because `src/engine/ai/` is barred
   * from importing the flags module — a rollout that reached the rules would
   * have two players deriving different boards from one log. Nought is the
   * shipped game, and is what everybody outside the experiment plays.
   */
  const waveDelay = useVariant<number, number>("wave-delay", 0);
  /**
   * The ruleset a fresh game opens on (§26 F1), resolved here for the same
   * reason as `waveDelay`: the flags module may not be read any nearer the
   * rules, so the value is settled in the shell and rides in the opening
   * command like any other setup. `undefined` is the shipped game.
   */
  const ruleset = useDefaultRuleset();

  /**
   * What a click means. **One call, and one hop**: the object is rebuilt every
   * render, so each zone takes it whole rather than having forty callbacks
   * threaded through it, and no zone taking it may be memoised.
   */
  const board_ = useBoardInteraction(
    (square) => board.current?.pulse(square),
    controlling,
    tool === "build"
  );
  const { state } = board_;

  /**
   * Whose economy the chrome is about — the run's army, or the seat's.
   *
   * **The single definition**, kept here because three surfaces read it: the
   * readouts, the hotbar and the pause menu are views of one side and must
   * never disagree.
   */
  const hotbarSide = state.run?.side ?? board_.localSide;

  /**
   * Which way the board faces: the seat where there is one, and otherwise
   * whoever is on the clock. `useCameraLooks` carries the whole argument — the
   * short of it is that only hot-seat hands the camera over.
   */
  const viewpoint = seatedAs ?? state.position.turn;

  /**
   * What the View menu remembers. **One call**, here: `useCameraPrefs` is
   * `useState` over `localStorage`, so a second call site is a second copy that
   * drifts the first time somebody flips a checkbox.
   */
  const [camera, setCamera] = useCameraPrefs();
  /**
   * Everything that points the camera, and the one place that tells it what
   * the interface covers (`useCameraLooks`). The three callbacks it hands back
   * are stable for the life of the shell — the driver names them in the
   * dependency list of the effect that plays the opponent's turn.
   */
  const game = useGame();
  const { clockHealth, standardBoard } = useCameraLooks(
    game,
    board,
    state,
    camera,
    viewpoint
  );

  /**
   * Driving a game nobody is playing — the speed, the pause, the single step,
   * and dealing another board when this one draws (`useWatchTransport`).
   */
  const watch = useWatchTransport(watching, state.status, onRestart);

  // "You got a thing." A crown delivered used to grow the deck in silence and
  // a cleared stage used to be a chip changing number; both are the moments a
  // run is actually *for*. Single player only — outside a run there are no
  // cards to win and no stages to clear.
  //
  // Not while watching. A celebration takes the whole viewport and will not go
  // until it is clicked, which is the most intrusive control surface there is
  // — and a spectator won nothing. Left on, an unattended watched game queues
  // one per stage behind a plate covering the board it exists to show.
  //
  // **And not the stage card wherever the branch picker is on.** The two are
  // the same event described twice — the picker already reports what the front
  // cost, and by the time a camp has landed the player chose it — so leaving
  // both gives a front two modals back to back with the second one telling you
  // what you just decided.
  /**
   * A cleared front asks the player which way the board grows (PLAN §19b).
   *
   * **The same gate as the card it replaces**, and not `state.run` — which was
   * the first guess and is wrong about the game: `stageTurnsOver` is `!run ||
   * …`, so a scenario clears fronts and takes waves exactly as a run does, and
   * gating on the run would have left a scenario player watching camps arrive
   * while a run player chose them.
   *
   * **Never while watching.** There is nobody to ask, and there the automatic
   * chooser is not a fallback but the balance instrument §19.6 hazard 4 asks
   * for — an unattended game that stopped at every front would also queue a
   * modal over the board it exists to show.
   */
  const branching = Boolean(scenario) && !watching;
  const party = useCelebrations(
    state,
    log,
    Boolean(scenario) && !watching,
    !branching
  );

  /**
   * The run is over, or null while it is alive.
   *
   * No queue and no "seen" set, unlike a celebration: this is not an event
   * that happened once, it is a *state* the game is now in and stays in. There
   * is nothing after it to get back to, so it has no dismiss — the two buttons
   * on it are the only things left to do.
   *
   * `state.run` is what gates it, so a scenario and a watched game are covered
   * without either being named: neither has a run to lose.
   */
  const ended = endingOf(state);

  /**
   * The army about to lay down its arms, and what would stop it.
   *
   * Yours first — two armies can be under the diagnosis at once and the one
   * ending *your* run is not the one with the lower side number. `holdings` is
   * asked only when there is a banner to put the numbers on: it counts every
   * heap against every piece on the board, which is the expensive half of the
   * check that `destitute` short-circuits past for any healthy army.
   *
   * Here rather than in `BoardLayer` because the hint curriculum is quiet
   * while it stands — one reading, two readers.
   */
  const despair = worstDespair(despairView(state, board_.localSide));
  const despairEscape =
    despair?.mine ? holdings(state, despair.side) : undefined;

  /** Pointer over the board: what brings the file and rank labels up. */
  const [overBoard, setOverBoard] = useState(false);
  const solo = Boolean(scenario || state.run);

  // Single player: set the scenario up once, then let the opponent play Black.
  useScenarioOpening(scenario, seed, teams, ruleset, dispatch);

  // A run climbs to the difficulty you picked rather than opening at it: the
  // first camp is the one where you have a king, a few heaps and no idea what
  // a refinery is for, and meeting a full-strength opponent there is a wall
  // rather than a setting.
  const playing = state.run ? levelForStage(level, stagesCleared(state) + 1) : level;

  /**
   * How fast turns play back — **one number, and every clock takes it**.
   *
   * Three things multiply into it, and they are three different questions:
   *
   *  - the **watch transport**, which sets how fast a game nobody is playing
   *    runs.
   *  - the player's own **animation speed** from the View menu, which is a
   *    person saying how fast they like watching and so applies always.
   *
   * The crowd ramp is deliberately *not* in here: `tempoOf` is a fact about the
   * board and the renderer already reads it for itself.
   *
   * **Both clocks, or it is half a control.** The driver deals a plan out at
   * `STEP_MS / pace` and the renderer plays the board at this scale; a speed
   * reaching one of them leaves the pieces finishing and waiting, or the next
   * turn's commands landing on a board still moving.
   */
  const playbackPace = (watching ? watch.pace : 1) * snapSpeed(camera.animationSpeed);

  /**
   * Whoever is playing while you are not: the opponent and the run director
   * (`useOpponentDriver`).
   *
   * **Unconditional, and never a component.** The driver's memory — what it was
   * doing, its depth banks, its belt ages — lives on this component instance,
   * so anything that could unmount it is an opponent that forgets.
   */
  const front = useOpponentDriver(game, {
    enabled: Boolean(scenario),
    // Watching: nobody is sitting here, so the driver plays for every side.
    // On the line that is null. The player is always White otherwise.
    playerSide: watching ? null : (0 as Side),
    level: playing,
    pace: playbackPace,
    tuning,
    paused: watching && watch.paused,
    step: watch.step,
    branching,
    // How much head start the camera gets, which is the playback tempo it is
    // already being drawn at plus its own extra third. Read for the side about
    // to move, since that is the turn this driver is about to play.
    lead: leadFor(tempoOf(state, state.position.turn)),
    // "Opponent plays fast": deal the whole turn out at once rather than a
    // command at a time. The renderer takes the other half of the same
    // setting — see `staging` on the canvas below.
    fast: camera.opponentFast,
    // How much animation time the board is managing per second of wall clock,
    // so the driver stops dealing commands faster than they can be drawn. Read
    // through a callback rather than passed as a value: it changes every frame
    // and the driver wants it once a turn, so a prop would re-run the effect
    // constantly for a number nobody was going to look at yet.
    clockHealth,
    // A cleared front holds open for this many turns before the next camp
    // lands. Zero everywhere the experiment is not running, which is the old
    // single-beat boundary exactly.
    waveDelay
  });
  /**
   * One thing to try, from §16's curriculum. Quiet while watching — a
   * spectator is not learning to play — and quiet while a modal already wants
   * reading, since one thing asks to be read at a time.
   */
  const hint = useHints(state, log, board_.localSide, {
    watching,
    busy: Boolean(party.current) || Boolean(front.branch) || Boolean(despair)
  });

  const modalUp =
    Boolean(party.current) ||
    Boolean(front.branch) ||
    Boolean(ended);
  // Escape peels one layer before it opens the menu (`useEscapeLayers`).
  useEscapeLayers(pause, modalUp, board_.dismissable, board_.dismiss, setPause);

  return (
    <ShellModeProvider
      watching={watching}
      scenario={scenario}
      // **The single definition**, passed down rather than re-derived: what you
      // may move and which way the camera faces are the same answer, and a
      // second derivation is how they come apart.
      seatedAs={seatedAs}
      controlling={controlling}
      solo={solo}
      branching={branching}
      // Boolean(scenario), and not `machineToMove` — this says a machine plays
      // the other armies, which is settled when the game opens; whose clock it
      // is right now is a question about the turn and stays out of the context.
      machinePlays={Boolean(scenario)}
      level={level}
      playing={playing}
      onExit={onExit}
      onRestart={onRestart}
      onLoadLevel={onLoadLevel}
      onPrivacy={onPrivacy}
    >
      <BoardHandleProvider board={board}>
        <Screen>
          {/* **The top of the screen, floating.** The header bar died at U3
              (PLAN-UI §28.3): identity, diagnostics and the Rules/Costs/
              Feedback/Restart buttons are the pause menu's now, the readouts
              took the left corner and the turn row the right. Nothing here is a
              row the board has to sit under.

              **Always, watching included** (U8-A): a watched game gets one pill
              per living army in place of the stock pill, because reading the
              economies against each other is the whole of what there is to do
              with a game nobody is playing. */}
          <Readouts
            scorePill={scorePanel}
            watching={watching}
          />

          <HotbarZone />

          <InspectorZone />

          <RailZone
            tool={tool}
            onTool={setTool}
            camera={camera}
            onCamera={setCamera}
          />

          <HandZone tool={tool} notice={notice} />

          <TopRight
            board_={board_}
            watch={watch}
            onPause={() => setPause("settings")}
          />

          <BoardLayer
            board_={board_}
            despair={despair}
            despairEscape={despairEscape}
            onCelebrate={party.preview}
            camera={camera}
            paceScale={playbackPace}
            maxFps={maxFps}
            tool={tool}
            onHover={setOverBoard}
          />

          <TurnBannerZone />

          <BoardOverlays overBoard={overBoard} hint={hint} />

          <PauseMenuZone
            screen={pause}
            onScreen={setPause}
            side={hotbarSide}
            camera={camera}
            onCamera={setCamera}
            cameraFixed={standardBoard}
          />

          <ModalStack
            party={party}
            front={front}
            ended={ended}
          />
        </Screen>
      </BoardHandleProvider>
    </ShellModeProvider>
  );
}
